import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import {
  UpdateLineItemDto,
  InvoiceWithRelationsDto,
  ResponseDto,
} from "@mimber/schema"
import { invoicesKeys } from "../invoices.utils"

interface UpdateInvoiceLineItemOptions extends Omit<
  UseMutationOptions<
    ResponseDto<InvoiceWithRelationsDto>,
    Error,
    UpdateLineItemDto
  >,
  "mutationFn"
> {}

export const useUpdateInvoiceLineItem = (
  invoiceUuid: string,
  lineItemUuid: string,
  options: UpdateInvoiceLineItemOptions = {}
): UseMutationResult<
  ResponseDto<InvoiceWithRelationsDto>,
  Error,
  UpdateLineItemDto
> => {
  guardAppUsage("mimber-organization", "useUpdateInvoiceLineItem")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: UpdateLineItemDto) => {
      const response = await server.patch<ResponseDto<InvoiceWithRelationsDto>>(
        `invoices/${invoiceUuid}/line-items/${lineItemUuid}`,
        data
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: invoicesKeys.findOne(invoiceUuid),
      })
      queryClient.invalidateQueries({ queryKey: invoicesKeys.findAll() })
      options?.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
